import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {
  AccountNameForm,
  type AccountNameFormRenderModel,
} from "@bw/shared/components";

export function AccountNameEditor() {
  return <AccountNameForm render={renderForm} />;
}

/** Audience-native UI; the shared controller owns validation and confirmation. */
function renderForm(model: AccountNameFormRenderModel) {
  const { ref: nameRef, ...nameField } = model.name.registration;
  const { ref: passwordRef, ...passwordField } = model.password.registration;
  return (
    <Paper component="section" variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
      <Stack component="form" noValidate onSubmit={model.onSubmit} spacing={2}>
        <Typography component="h2" variant="h6">
          Account name
        </Typography>
        <Typography color="text.secondary">
          Used for your sign-in account. Your public player name and business
          details stay unchanged.
        </Typography>
        <Typography variant="body2">Confirming {model.email}</Typography>
        <Typography variant="body2" color="text.secondary">
          Enter your password to confirm. Google-only account names can&apos;t
          be edited here yet.
        </Typography>
        {model.error ? (
          <Alert severity="error" role="alert">
            {model.error}
          </Alert>
        ) : null}
        {model.saved ? (
          <Alert severity="success" role="status">
            Account name saved. Your current sign-in still has the previous
            account name. Sign out and sign in again when you&apos;re ready.
          </Alert>
        ) : null}
        <TextField
          {...nameField}
          inputRef={nameRef}
          label="Account name"
          autoComplete="nickname"
          required
          fullWidth
          disabled={model.pending || model.saved}
          error={Boolean(model.name.error)}
          helperText={model.name.error}
        />
        <TextField
          {...passwordField}
          inputRef={passwordRef}
          label="Account password"
          type="password"
          autoComplete="current-password"
          required
          fullWidth
          disabled={model.pending || model.saved}
          error={Boolean(model.password.error)}
          helperText={model.password.error}
        />
        <Button
          type="submit"
          variant="contained"
          disabled={model.pending || model.saved}
        >
          {model.saved
            ? "Account name saved"
            : model.pending
              ? "Saving account name…"
              : "Save account name"}
        </Button>
      </Stack>
    </Paper>
  );
}
